import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icon/index.js';
import { apiGet, getApiOrigin } from '../../lib/api.js';
import { GatorMarkCircle } from './GatorMarkCircle.js';

type MaintenanceKind = 'MAINTENANCE' | 'RELEASE' | 'ANNOUNCEMENT';

type ActiveNotice = {
  id: string;
  kind: MaintenanceKind;
  titleEn: string;
  titlePl: string;
  bodyEn: string | null;
  bodyPl: string | null;
  startsAt: string;
  endsAt: string | null;
  imageUrl: string | null;
  updatedAt: string;
};

const DISMISS_PREFIX = 'ig-maintenance-dismissed-';
const DIALOG_DELAY_MS = 1200;

/** Dismissal is keyed on id + updatedAt: editing a notice re-surfaces it. */
function dismissKey(notice: ActiveNotice): string {
  return `${DISMISS_PREFIX}${notice.id}-${new Date(notice.updatedAt).getTime()}`;
}

function isDismissed(notice: ActiveNotice): boolean {
  try {
    return localStorage.getItem(dismissKey(notice)) === '1';
  } catch {
    return false;
  }
}

function markDismissed(notice: ActiveNotice) {
  try {
    localStorage.setItem(dismissKey(notice), '1');
  } catch {
    /* storage unavailable — the notice simply reappears next visit */
  }
}

function resolveImage(url: string | null): string | null {
  if (!url) return null;
  const origin = getApiOrigin() ?? '';
  return url.startsWith('/') ? `${origin}${url}` : url;
}

function MaintenanceDialog({ notice, onDismiss }: { notice: ActiveNotice; onDismiss: () => void }) {
  const { t, i18n } = useTranslation();
  const pl = i18n.language?.startsWith('pl') ?? false;
  const image = resolveImage(notice.imageUrl);

  const copy = useMemo(
    () => ({
      title: pl ? notice.titlePl : notice.titleEn,
      body: pl ? notice.bodyPl : notice.bodyEn,
    }),
    [notice, pl],
  );

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(pl ? 'pl-PL' : 'en-US', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(iso));

  const eyebrow =
    notice.kind === 'RELEASE'
      ? t('maintenance.kind.release', { defaultValue: 'New release' })
      : notice.kind === 'ANNOUNCEMENT'
        ? t('maintenance.kind.announcement', { defaultValue: 'Announcement' })
        : t('maintenance.kind.maintenance', { defaultValue: 'Scheduled maintenance' });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onDismiss]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
      onClick={onDismiss}
    >
      <div
        className="relative w-full max-w-[460px] overflow-hidden rounded-lg border border-ig-border bg-ig-bg-secondary shadow-2xl motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-4 motion-safe:duration-400"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('common.close', { defaultValue: 'Close' })}
          className="absolute right-3 top-3 z-10 rounded-full bg-black/40 p-1.5 text-white/90 backdrop-blur transition hover:bg-black/60"
        >
          <Icon name="x" className="h-4 w-4" />
        </button>

        {image ? (
          <img src={image} alt="" className="max-h-[220px] w-full object-cover" />
        ) : (
          <div className="flex h-24 items-center justify-center bg-gradient-to-br from-ig-bg-tertiary to-ig-accent-subtle">
            <GatorMarkCircle className="h-12 w-12" alt="" />
          </div>
        )}
        {/* Accent signature bar */}
        <div className="h-[3px] w-full" style={{ background: 'linear-gradient(90deg, #128A3E, #1DB954 45%, #6EE7A0)' }} />

        <div className="flex flex-col gap-3 p-6">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-ig-warning/12 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-ig-warning">
            <Icon name="triangle-alert" className="h-3 w-3" />
            {eyebrow}
          </span>
          <h2 className="font-display text-[26px] font-light leading-[1.15] text-ig-text-primary">{copy.title}</h2>
          {copy.body ? (
            <p className="whitespace-pre-line font-sans text-[14px] leading-[1.65] text-ig-text-secondary">{copy.body}</p>
          ) : null}

          <div className="rounded-lg border border-ig-border bg-ig-bg-tertiary p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ig-text-tertiary">
              {t('maintenance.windowLabel', { defaultValue: 'Planned window' })}
            </p>
            <p className="mt-2 flex items-center gap-2 font-sans text-[13px] font-medium text-ig-text-primary">
              <Icon name="clock-3" className="h-3.5 w-3.5 text-ig-accent" />
              {t('maintenance.from', { defaultValue: 'From' })}: {formatDate(notice.startsAt)}
            </p>
            <p className="mt-1 flex items-center gap-2 font-sans text-[13px] font-medium text-ig-text-primary">
              <Icon name="check-circle-2" className="h-3.5 w-3.5 text-ig-accent" />
              {t('maintenance.until', { defaultValue: 'Until' })}:{' '}
              {notice.endsAt
                ? formatDate(notice.endsAt)
                : t('maintenance.untilFurtherNotice', { defaultValue: 'until further notice' })}
            </p>
          </div>

          <button
            type="button"
            onClick={onDismiss}
            className="mt-1 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-ig-accent px-6 font-sans text-[14px] font-semibold text-[var(--ig-accent-text)] transition hover:brightness-110"
          >
            {t('maintenance.gotIt', { defaultValue: 'Got it' })}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mounted in the logged-in app shell: fetches the active admin-managed
 * maintenance notice and shows it as a dialog. Dismissal persists per
 * notice-version in localStorage, so users see it once per edit.
 */
export function MaintenanceNoticeHost() {
  const [notice, setNotice] = useState<ActiveNotice | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void apiGet<{ notice: ActiveNotice | null }>('/maintenance/active', { suppressErrorToast: true })
      .then((res) => {
        if (cancelled) return;
        const active = res.notice;
        if (active && !isDismissed(active)) {
          setNotice(active);
          window.setTimeout(() => {
            if (!cancelled) setVisible(true);
          }, DIALOG_DELAY_MS);
        }
      })
      .catch(() => {
        /* the notice is informational — never break the app shell */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!notice || !visible) return null;

  return (
    <MaintenanceDialog
      notice={notice}
      onDismiss={() => {
        markDismissed(notice);
        setVisible(false);
        setNotice(null);
      }}
    />
  );
}
